import { Flame, PauseCircle, Archive, Edit, ChevronRight, Dumbbell } from 'lucide-react'
import type { Task } from '@/types'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

export type HabitRowVariant = 'active' | 'paused' | 'archived'

/**
 * One habit as a single ~48px row, for phones only.
 *
 * The desktop card carries the title, an edit button, a "Habit" badge, the
 * frequency, the project, and a "click to view analytics" footer — six lines
 * and ~200px each. In a three-column grid that's fine; on a 390px phone eight
 * habits became four screens of scrolling to read eight words.
 *
 * What survives here is what you scan a habit list *for*: which habit, and how
 * often. The project, the description and the analytics live one tap away in
 * the habit's own page, which is where the row goes.
 */
export function HabitRow({
  habit,
  variant = 'active',
  onOpen,
  onEdit,
}: {
  habit: Task
  variant?: HabitRowVariant
  onOpen: () => void
  onEdit?: () => void
}) {
  // The Workout habit (ticked by finishing a workout) reads as the gym.
  const Icon = variant === 'archived' ? Archive : variant === 'paused' ? PauseCircle
    : habit.habit_kind === 'workout' ? Dumbbell : Flame
  const iconColor =
    variant === 'archived'
      ? 'text-purple-400'
      : variant === 'paused'
        ? 'text-muted-foreground'
        : 'text-orange-500'

  return (
    <div
      className={cn(
        'flex items-center gap-2 rounded-lg border bg-card px-3 py-2 active:bg-accent/50',
        variant !== 'active' && 'opacity-75',
      )}
      onClick={onOpen}
    >
      <Icon className={cn('h-4 w-4 shrink-0', iconColor)} />
      <span className="min-w-0 flex-1 truncate text-sm font-medium">{habit.title}</span>
      <span className="shrink-0 text-xs text-muted-foreground">{describeCadence(habit)}</span>
      {onEdit && variant !== 'archived' ? (
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0"
          onClick={(e) => {
            e.stopPropagation()
            onEdit()
          }}
          aria-label={`Edit ${habit.title}`}
        >
          <Edit className="h-4 w-4" />
        </Button>
      ) : (
        <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
      )}
    </div>
  )
}

/**
 * "daily", "Mon, Thu", "weekly" — short enough to sit at the end of a row
 * without stealing width from the title. Full day names are spelled out on the
 * desktop card; three letters is enough to recognise a pattern you set.
 */
function describeCadence(habit: Task): string {
  const recurrence = habit.recurrence
  if (!recurrence?.frequency) return ''
  const days = recurrence.days_of_week ?? []
  if (recurrence.frequency === 'daily' || days.length === 0 || days.length === 7) {
    return recurrence.frequency
  }
  return days.map((d) => d.slice(0, 3)).join(', ')
}
